import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { brandConfig } from '../design/theme';

// =============================================================================
// OfficeWindowAnimation
// -----------------------------------------------------------------------------
// A single, reusable ~16:9 Office window that shows the whole app running, with
// the Restack add-in sidebar INTEGRATED as a column INSIDE the window body (the
// way the home-page PowerPoint hero does it: one window, never a detached card).
//
// It faithfully emulates the home hero's agent step trail: a user prompt bubble
// followed by a staggered, top-to-bottom progress trail where each step morphs
// from "● Active..." (with an expanded detail panel) to "✓ Done" once its window
// finishes, and the final step stays in-progress. The proven 15s loop timing is
// ported here under a co-located, app-colourable `rs-owa-*` namespace so the
// home hero (different route, untouched styles) keeps working exactly as-is.
//
// Props:
//   app       'powerpoint' | 'excel' | 'word': selects the title-bar colour,
//             ribbon tabs, status-bar copy, and accent of the window chrome.
//   scenario  string key, selects filename, ribbon, main content, and the
//             sidebar's user prompt + step trail. Implemented scenarios:
//               'pptx-deck'    (powerpoint) precise consulting/PE market-sizing
//               'pptx-generic' (powerpoint) plainer generalised "any document"
//               'excel-lbo'    (excel) a formula-driven LBO model
//               'word-report'  (word) a diligence report with tracked redline
//
// Design system: PowerPoint orange (#C9461F -> #B83B1D), Excel green #217346,
// Word blue #35548A; ACCENT #B83B1D; INK #2E140A; theme typography; MUI sx.
// Unified radius: 14px outer frame, 8px inner cards. No em dashes in copy.
// =============================================================================

const ACCENT = '#B83B1D';

const APP_THEME = {
  powerpoint: {
    titlebar: 'linear-gradient(180deg, #C9461F 0%, #B83B1D 100%)',
    color: '#B83B1D',
    label: 'POWERPOINT',
    badge: 'P',
    ribbon: ['File', 'Home', 'Insert', 'Draw', 'Design', 'Transitions', 'Animations', 'Slide Show', 'Review', 'View'],
  },
  excel: {
    titlebar: 'linear-gradient(180deg, #2A8C56 0%, #217346 100%)',
    color: '#217346',
    label: 'EXCEL',
    badge: 'X',
    ribbon: ['File', 'Home', 'Insert', 'Draw', 'Formulas', 'Data', 'Review', 'View'],
  },
  word: {
    titlebar: 'linear-gradient(180deg, #41608F 0%, #35548A 100%)',
    color: '#35548A',
    label: 'WORD',
    badge: 'W',
    ribbon: ['File', 'Home', 'Insert', 'Draw', 'Design', 'Layout', 'References', 'Review', 'View'],
  },
};

// -----------------------------------------------------------------------------
// SCENARIOS. Each owns: which app, filename, status-bar copy, the main-content
// renderer key, the sidebar user prompt, the closing trail summary line, and an
// ordered list of step cards. Each step card supplies an active/done label and
// an optional detail panel (a list of small rows that reveal on a stagger).
//
// The step labels intentionally share the same through-line across all apps:
//   Reading -> Pulling sources -> Building -> Checking/Auditing/Fact-checking
//   -> Citing. That sameness is the product story (one agent, every surface).
// -----------------------------------------------------------------------------

const SCENARIOS = {
  // ---- PowerPoint: precise consulting / PE market-sizing deck ----------------
  'pptx-deck': {
    app: 'powerpoint',
    filename: 'Nordic_Market_Sizing.pptx',
    status: 'Slide 4 of 12',
    main: 'pptxDeck',
    prompt: 'Build a market sizing slide for Nordic fintech entry. Pull the numbers from my Excel model and the thesis from Word.',
    attachment: { badge: 'PDF', name: 'Nordic_Brief.pdf', color: ACCENT },
    summary: 'Slide built. 4 figures cited.',
    steps: [
      {
        active: 'Reading your workspace...', done: 'Read Excel, Word + deck',
        detail: [
          { tag: 'XLSX', tagColor: '#1B7A4F', tagBg: 'rgba(33,115,70,0.14)', text: 'Nordic_Market_Sizing.xlsx' },
          { tag: 'DOCX', tagColor: '#2B579A', tagBg: 'rgba(53,84,138,0.14)', text: 'Nordic_Market_Sizing.docx' },
          { tag: 'DECK', text: 'Slide 2 · Executive Summary' },
          { tag: 'DECK', text: 'Slide 7 · Methodology' },
        ],
      },
      {
        active: 'Pulling sources...', done: 'Scanned 87 sources',
        detail: [
          { check: true, text: 'Nordic Capital Markets 2026' },
          { check: true, text: 'ECB Fintech Report Q3' },
          { check: true, text: 'Dealroom, Statista + 84 more' },
        ],
      },
      {
        active: 'Building the slide...', done: 'Slide built · chart rendered',
        detail: [
          { check: true, text: 'Action title + TAM / SAM / SOM' },
          { check: true, text: 'Revenue projection chart' },
          { check: true, text: 'Footnote source bar' },
        ],
      },
      {
        active: 'Running quality check...', done: 'Quality check passed',
        detail: [
          { check: true, text: 'Font sizes ≥ 14pt' },
          { check: true, text: 'Colour contrast OK' },
          { check: true, text: 'Numbers tie to the model' },
        ],
      },
      {
        active: 'Citing every figure...', done: 'Citing every figure',
        detail: [
          { check: true, text: 'TAM → Nordic Capital Markets' },
          { check: true, text: 'Growth → ECB Fintech Q3' },
          { spinner: true, text: 'Linking footnotes...' },
        ],
        final: true,
      },
    ],
  },

  // ---- PowerPoint: plainer, generalised "any document" deck ------------------
  'pptx-generic': {
    app: 'powerpoint',
    filename: 'Marketing_Plan_2026.pptx',
    status: 'Slide 3 of 9',
    main: 'pptxGeneric',
    prompt: 'Draft a one-slide marketing plan overview for the spring campaign. Keep it clean and on-brand.',
    summary: 'Slide built. Ready to present.',
    steps: [
      {
        active: 'Reading your workspace...', done: 'Read the brief + deck',
        detail: [
          { tag: 'DOCX', tagColor: '#2B579A', tagBg: 'rgba(53,84,138,0.14)', text: 'Campaign_Brief.docx' },
          { tag: 'DECK', text: 'Slide 1 · Title' },
          { tag: 'DECK', text: 'Slide 2 · Goals' },
        ],
      },
      {
        active: 'Gathering inputs...', done: 'Gathered 3 inputs',
        detail: [
          { check: true, text: 'Campaign goals' },
          { check: true, text: 'Audience + channels' },
          { check: true, text: 'Timeline' },
        ],
      },
      {
        active: 'Building the slide...', done: 'Slide built · 3 sections',
        detail: [
          { check: true, text: 'Title + objective' },
          { check: true, text: 'Channel mix' },
          { check: true, text: 'Timeline bar' },
        ],
      },
      {
        active: 'Checking the layout...', done: 'Layout check passed',
        detail: [
          { check: true, text: 'On-brand colours' },
          { check: true, text: 'Readable text sizes' },
          { check: true, text: 'Aligned to grid' },
        ],
      },
      {
        active: 'Tidying up...', done: 'Tidying up',
        detail: [
          { check: true, text: 'Consistent spacing' },
          { spinner: true, text: 'Polishing...' },
        ],
        final: true,
      },
    ],
  },

  // ---- Excel: a formula-driven LBO model ------------------------------------
  'excel-lbo': {
    app: 'excel',
    filename: 'Project_Atlas_LBO.xlsx',
    status: 'Model',
    main: 'excelLbo',
    prompt: 'Build an LBO for Project Atlas: 5.5x entry, 60% debt, 5-year hold. Source every cell.',
    attachment: { badge: 'PDF', name: 'Atlas_CIM.pdf', color: '#1B7A4F' },
    summary: 'Model built. 18 cells sourced.',
    steps: [
      {
        active: 'Reading your workbook...', done: 'Read the workbook',
        detail: [
          { tag: 'SHEET', tagColor: '#1B7A4F', tagBg: 'rgba(33,115,70,0.14)', text: 'Model · Sources & Uses' },
          { tag: 'SHEET', tagColor: '#1B7A4F', tagBg: 'rgba(33,115,70,0.14)', text: 'Debt · schedule' },
          { tag: 'SHEET', tagColor: '#1B7A4F', tagBg: 'rgba(33,115,70,0.14)', text: 'Returns · IRR / MOIC' },
        ],
      },
      {
        active: 'Pulling comps and sources...', done: 'Pulled comps + filings',
        detail: [
          { check: true, text: 'Atlas CIM · EBITDA bridge' },
          { check: true, text: 'Comps · EV / EBITDA' },
          { check: true, text: 'SOFR curve + credit terms' },
        ],
      },
      {
        active: 'Building the LBO...', done: 'LBO built · entry to exit',
        detail: [
          { check: true, text: 'Sources & Uses balanced' },
          { check: true, text: 'Entry EV · 5.5x EBITDA' },
          { check: true, text: 'Exit EV · 6.0x EBITDA' },
        ],
      },
      {
        active: 'Wiring the debt schedule...', done: 'Debt schedule wired',
        detail: [
          { check: true, text: 'Term loan · cash sweep' },
          { check: true, text: 'Interest → cash flow' },
          { check: true, text: 'Net debt → returns' },
        ],
      },
      {
        active: 'Auditing formulas...', done: 'Formulas audited',
        detail: [
          { check: true, text: 'No hardcodes in calc cells' },
          { check: true, text: 'Balance sheet balances' },
          { check: true, text: 'IRR ties to cash flows' },
        ],
      },
      {
        active: 'Sourcing every cell...', done: 'Sourcing every cell',
        detail: [
          { check: true, text: 'EBITDA → Atlas CIM' },
          { check: true, text: 'Exit multiple → comps' },
          { spinner: true, text: 'Tagging cell notes...' },
        ],
        final: true,
      },
    ],
  },

  // ---- Word: a diligence report with a tracked-change redline ---------------
  'word-report': {
    app: 'word',
    filename: 'Atlas_Diligence_Memo.docx',
    status: 'Page 2 of 14',
    main: 'wordReport',
    prompt: 'Tighten the market section of the diligence memo and fact-check every claim against the sources.',
    attachment: { badge: 'PDF', name: 'Atlas_Data_Room.pdf', color: '#2B579A' },
    summary: 'Redline ready. Every claim cited.',
    steps: [
      {
        active: 'Reading your document...', done: 'Read the memo',
        detail: [
          { tag: 'DOCX', tagColor: '#2B579A', tagBg: 'rgba(53,84,138,0.14)', text: 'Section 3 · Market' },
          { tag: 'DOCX', tagColor: '#2B579A', tagBg: 'rgba(53,84,138,0.14)', text: 'Section 4 · Competition' },
          { tag: 'PDF', tagColor: ACCENT, tagBg: 'rgba(184,59,29,0.10)', text: 'Atlas_Data_Room.pdf' },
        ],
      },
      {
        active: 'Researching sources...', done: 'Researched sources',
        detail: [
          { check: true, text: 'Market size · 2 sources agree' },
          { check: true, text: 'Growth rate · filings' },
          { check: true, text: 'Competitor set · confirmed' },
        ],
      },
      {
        active: 'Drafting...', done: 'Draft tightened',
        detail: [
          { check: true, text: 'Cut hedging language' },
          { check: true, text: 'Sharpened the claim' },
          { check: true, text: 'Kept the structure' },
        ],
      },
      {
        active: 'Redlining as tracked changes...', done: 'Tracked changes added',
        detail: [
          { check: true, text: 'Deletions marked' },
          { check: true, text: 'Insertions marked' },
          { check: true, text: 'Change bar in the margin' },
        ],
      },
      {
        active: 'Fact-checking claims...', done: 'Claims fact-checked',
        detail: [
          { check: true, text: 'Market size ✓ verified' },
          { check: true, text: 'Growth rate ✓ verified' },
          { check: true, text: 'No unsupported claims' },
        ],
      },
      {
        active: 'Citing every claim...', done: 'Citing every claim',
        detail: [
          { check: true, text: 'Footnote 4 → data room' },
          { check: true, text: 'Footnote 5 → filing' },
          { spinner: true, text: 'Linking citations...' },
        ],
        final: true,
      },
    ],
  },
};

// =============================================================================
// MAIN-CONTENT RENDERERS. Each returns the JSX that sits in the centre column
// of the window body, between the left nav and the integrated sidebar. They are
// app-specific (a deck slide, an Excel grid, a Word page) but all use the
// unified radius and the design-system palette.
// =============================================================================

function PptxDeckMain() {
  return (
    <Box className="rs-owa-slide">
      <Box className="rs-owa-slide__title">Nordic payments reach $340M by 2029 at 41% CAGR, 2x EU growth</Box>
      <Box className="rs-owa-slide__subtitle">Payment Processing &amp; Digital Banking | Q4 2026 Analysis</Box>
      <Box className="rs-owa-slide__divider" />
      <Box className="rs-owa-slide__metrics">
        {[
          { label: 'TAM', value: '$4.2B', cite: '1', desc: 'Total addressable: Nordics payments + digital banking' },
          { label: 'SAM', value: '$1.8B', cite: '2', desc: 'Serviceable: B2B SaaS-accessible segment' },
          { label: 'SOM', value: '$340M', cite: '3', desc: 'Obtainable: by 2029 at 3.2% penetration' },
        ].map((m, i) => (
          <Box key={m.label} className={`rs-owa-slide__metric rs-owa-slide__metric--${i + 1}`}>
            <span className="rs-owa-slide__metric-label">{m.label}</span>
            <span className="rs-owa-slide__metric-value">{m.value}<sup className="rs-owa-slide__cite">{m.cite}</sup></span>
            <span className="rs-owa-slide__metric-desc">{m.desc}</span>
          </Box>
        ))}
      </Box>
      <Box className="rs-owa-slide__chart-wrap">
        <Box className="rs-owa-slide__chart-head">
          <span className="rs-owa-slide__chart-title">Revenue Projection ($M)</span>
          <span className="rs-owa-slide__chart-cagr">CAGR 41%</span>
        </Box>
        <Box className="rs-owa-slide__chart">
          {[
            { h: 35, v: '$120M', y: '2026' },
            { h: 54, v: '$185M', y: '2027' },
            { h: 76, v: '$260M', y: '2028' },
            { h: 100, v: '$340M', y: '2029' },
          ].map((b, i) => (
            <Box key={b.y} className={`rs-owa-slide__bar rs-owa-slide__bar--${i + 1}`} style={{ '--bar-h': `${b.h}%` }}>
              <span className="rs-owa-slide__bar-value">{b.v}</span>
              <span className="rs-owa-slide__bar-label">{b.y}</span>
            </Box>
          ))}
        </Box>
      </Box>
      <Box className="rs-owa-slide__sources">
        {['Nordic Capital Markets 2026', 'ECB Fintech Q3 2026', 'Dealroom Nordic', 'Statista Payments 2026'].map((s, i) => (
          <Box key={s} className={`rs-owa-slide__source rs-owa-slide__source--${i + 1}`}>
            <span className="rs-owa-slide__source-num">{i + 1}</span>{s}
          </Box>
        ))}
      </Box>
      <Box className="rs-owa-slide__pagenum">4</Box>
    </Box>
  );
}

function PptxGenericMain() {
  return (
    <Box className="rs-owa-slide rs-owa-slide--generic">
      <Box className="rs-owa-slide__title rs-owa-slide__title--generic">Spring Campaign: grow trial signups 30% in Q2</Box>
      <Box className="rs-owa-slide__subtitle">Marketing Plan Overview | 2026</Box>
      <Box className="rs-owa-slide__divider rs-owa-slide__divider--generic" />
      <Box className="rs-owa-slide__cols">
        {[
          { head: 'Objective', lines: 3 },
          { head: 'Channel mix', lines: 4 },
          { head: 'Timeline', lines: 3 },
        ].map((c, i) => (
          <Box key={c.head} className={`rs-owa-slide__col rs-owa-slide__col--${i + 1}`}>
            <span className="rs-owa-slide__col-head">{c.head}</span>
            {Array.from({ length: c.lines }).map((_, k) => (
              <span key={k} className="rs-owa-slide__col-line" style={{ width: `${88 - k * 12}%` }} />
            ))}
          </Box>
        ))}
      </Box>
      <Box className="rs-owa-slide__timeline">
        {[18, 30, 22, 30].map((w, i) => (
          <Box key={i} className={`rs-owa-slide__timeline-seg rs-owa-slide__timeline-seg--${i + 1}`} style={{ flexBasis: `${w}%` }} />
        ))}
      </Box>
      <Box className="rs-owa-slide__pagenum">3</Box>
    </Box>
  );
}

function ExcelLboMain() {
  // A believable LBO read: Sources & Uses on the left, Returns on the right,
  // a formula bar above, a pulsing computed IRR cell, and sheet tabs below.
  const rows = [
    ['1', 'Sources & Uses', '$m', 'Returns', ''],
    ['2', 'Term Loan B', '660.0', 'Entry EV', '1,100'],
    ['3', 'Sponsor Equity', '440.0', 'Exit EV', '1,584'],
    ['4', 'Total Sources', '1,100', 'Net Debt', '(512)'],
    ['5', 'Purchase EV', '1,100', 'MOIC', '2.4x'],
    ['6', 'Fees', '22.0', 'IRR', '19.3%'],
  ];
  return (
    <Box className="rs-owa-xl">
      <Box className="rs-owa-xl__formula">
        <span className="rs-owa-xl__cellref">E6</span>
        <span className="rs-owa-xl__fx">fx</span>
        <span className="rs-owa-xl__formula-text">=IRR(Returns!C5:H5)</span>
        <Box className="rs-owa-xl__sweep" />
      </Box>
      <Box className="rs-owa-xl__grid">
        {['', 'A', 'B', 'C', 'D'].map((c, i) => (
          <Box key={`h${i}`} className="rs-owa-xl__colhead">{c}</Box>
        ))}
        {rows.flatMap((row, r) => row.map((v, c) => {
          const isRowNum = c === 0;
          const isHeader = r === 0;
          const isLabel = c === 1 || c === 3;
          const isNum = (c === 2 || c === 4) && !isHeader;
          const isIrr = r === 5 && c === 4; // IRR computed cell
          const cls = [
            'rs-owa-xl__cell',
            isRowNum ? 'rs-owa-xl__cell--rownum' : '',
            isHeader ? 'rs-owa-xl__cell--header' : '',
            isLabel && !isHeader ? 'rs-owa-xl__cell--label' : '',
            isNum ? 'rs-owa-xl__cell--num' : '',
            isIrr ? 'rs-owa-xl__cell--calc' : '',
          ].filter(Boolean).join(' ');
          return (
            <Box key={`${r}-${c}`} className={cls}>
              {isIrr ? <span className="rs-owa-xl__calc-val">{v}</span> : v}
            </Box>
          );
        }))}
        {/* Empty rows below the model so the grid fills the window like a real
            spreadsheet (gridlines all the way down) instead of stretching. */}
        {Array.from({ length: 14 }, (_, i) => {
          const rn = rows.length + 1 + i;
          return [String(rn), '', '', '', ''].map((v, c) => (
            <Box key={`empty-${rn}-${c}`} className={`rs-owa-xl__cell${c === 0 ? ' rs-owa-xl__cell--rownum' : ''}`}>{v}</Box>
          ));
        })}
      </Box>
      <Box className="rs-owa-xl__tabs">
        {['Model', 'Debt', 'Returns'].map((t, i) => (
          <span key={t} className={`rs-owa-xl__tab ${i === 0 ? 'rs-owa-xl__tab--active' : ''}`}>{t}</span>
        ))}
        <span className="rs-owa-xl__tab-add">+</span>
      </Box>
    </Box>
  );
}

function WordReportMain() {
  return (
    <Box className="rs-owa-doc">
      <Box className="rs-owa-doc__page">
        <Box className="rs-owa-doc__h1">3. Market Opportunity</Box>
        <Box className="rs-owa-doc__para">
          Atlas sells diligence automation into the Nordic mid-market, where
          deal teams still reconcile data rooms by hand.
        </Box>
        <Box className="rs-owa-doc__line" style={{ width: '96%' }} />
        {/* Tracked-change paragraph: red strikethrough deletion replaced by a
            blue underlined insertion, with a blinking caret and a left change
            bar marking the edited block. */}
        <Box className="rs-owa-doc__redline">
          <span className="rs-owa-doc__changebar" />
          <span className="rs-owa-doc__para">
            The Nordic market is{' '}
            <span className="rs-owa-doc__del">arguably quite large and growing fast</span>
            <span className="rs-owa-doc__ins">$4.2B and growing 23% YoY<sup className="rs-owa-doc__sup">4</sup></span>
            <span className="rs-owa-doc__caret" />
            , roughly 2x the EU average
            <span className="rs-owa-doc__verified">verified</span>.
          </span>
        </Box>
        <Box className="rs-owa-doc__para">
          Penetration remains low: fewer than{' '}
          <span className="rs-owa-doc__del">a handful of</span>
          <span className="rs-owa-doc__ins">12% of</span> firms have
          automated review<sup className="rs-owa-doc__sup">5</sup>, leaving most
          of the segment open.
        </Box>
        <Box className="rs-owa-doc__line" style={{ width: '90%' }} />

        <Box className="rs-owa-doc__h2">4. Competitive Landscape</Box>
        <Box className="rs-owa-doc__para">
          Three incumbents share the category; none ships an integrated redline
          and citation layer<sup className="rs-owa-doc__sup">5</sup>.
        </Box>
        <Box className="rs-owa-doc__compare">
          <Box className="rs-owa-doc__crow rs-owa-doc__crow--head">
            <span>Vendor</span><span>Share</span><span>Cited redline</span>
          </Box>
          <Box className="rs-owa-doc__crow">
            <span>Atlas</span><span>n/a</span><span className="rs-owa-doc__yes">Yes</span>
          </Box>
          <Box className="rs-owa-doc__crow">
            <span>Incumbent A</span><span>~31%</span><span className="rs-owa-doc__no">No</span>
          </Box>
        </Box>
        <Box className="rs-owa-doc__line" style={{ width: '64%' }} />

        <Box className="rs-owa-doc__footnotes">
          <Box className="rs-owa-doc__fn-rule" />
          <Box className="rs-owa-doc__fn"><sup>4</sup> Nordic Capital Markets, Atlas data room (2026).</Box>
          <Box className="rs-owa-doc__fn"><sup>5</sup> Form S-1, competitor filing (2025), p.42.</Box>
        </Box>
      </Box>
    </Box>
  );
}

const MAIN_RENDERERS = {
  pptxDeck: PptxDeckMain,
  pptxGeneric: PptxGenericMain,
  excelLbo: ExcelLboMain,
  wordReport: WordReportMain,
};

// =============================================================================
// LEFT NAV. A slim app-appropriate rail inside the window: slide thumbnails for
// PowerPoint, a row gutter for Excel, a page rail for Word. Kept lightweight so
// the eye stays on the main content + the integrated sidebar.
// =============================================================================

function LeftNav({ app }) {
  if (app === 'powerpoint') {
    return (
      <Box className="rs-owa-nav rs-owa-nav--ppt">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <Box key={n} className={`rs-owa-nav__thumb ${n === 4 ? 'rs-owa-nav__thumb--active' : ''}`}>
            <span className="rs-owa-nav__thumb-num">{n}</span>
            <Box className="rs-owa-nav__thumb-slide" />
          </Box>
        ))}
      </Box>
    );
  }
  if (app === 'excel') {
    return (
      <Box className="rs-owa-nav rs-owa-nav--xl">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
          <Box key={n} className="rs-owa-nav__rownum">{n}</Box>
        ))}
      </Box>
    );
  }
  return (
    <Box className="rs-owa-nav rs-owa-nav--word">
      {[1, 2, 3].map((n) => (
        <Box key={n} className={`rs-owa-nav__page ${n === 2 ? 'rs-owa-nav__page--active' : ''}`}>
          <span className="rs-owa-nav__page-num">{n}</span>
        </Box>
      ))}
    </Box>
  );
}

// =============================================================================
// THE INTEGRATED SIDEBAR. Header + user prompt bubble + the staggered step
// trail. Ported from the home hero: each step morphs active -> done, the detail
// panel expands while active and collapses when done, and the final step stays
// in-progress (its detail keeps a live spinner row).
// =============================================================================

function Sidebar({ scenario, brand = 'restack' }) {
  const { prompt, attachment, steps, summary } = scenario;
  const isClaude = brand === 'claude';
  return (
    <Box className="rs-owa-sidebar">
      <Box className="rs-owa-sidebar__header">
        {isClaude
          ? <Box component="span" sx={{ width: 12, height: 12, borderRadius: '3px', background: 'rgba(46,20,10,0.22)' }} />
          : <brandConfig.icon sx={{ fontSize: '0.72rem', color: 'var(--owa-accent)' }} />}
        {isClaude ? 'Claude' : 'Restack AI'}
        <span className="rs-owa-sidebar__status-dot" />
      </Box>

      <Box className="rs-owa-sidebar__scroll">
        <Box className="rs-owa-msg">
          <Box className="rs-owa-msg__text">{prompt}</Box>
          {attachment && (
            <Box className="rs-owa-msg__attachments">
              <Box className="rs-owa-msg__attachment">
                <span className="rs-owa-msg__badge" style={{ background: attachment.color }}>{attachment.badge}</span>
                <span className="rs-owa-msg__name">{attachment.name}</span>
              </Box>
            </Box>
          )}
        </Box>

        <Box className="rs-owa-steps">
          {steps.map((step, i) => (
            <Box key={i} className={`rs-owa-step rs-owa-step--${i + 1} ${step.final ? 'rs-owa-step--final' : ''}`}>
              <Box className="rs-owa-step__row">
                <Box className="rs-owa-step__icon-stack">
                  <span className="rs-owa-step__icon rs-owa-step__icon--spinner" />
                  <span className="rs-owa-step__icon rs-owa-step__icon--check">&#10003;</span>
                </Box>
                <Box className="rs-owa-step__label-stack">
                  <span className="rs-owa-step__label rs-owa-step__label--active">{step.active}</span>
                  <span className="rs-owa-step__label rs-owa-step__label--done">{step.done}</span>
                </Box>
              </Box>
              {step.detail && (
                <Box className="rs-owa-step__detail">
                  {step.detail.map((d, k) => (
                    <Box key={k} className={`rs-owa-detail-item rs-owa-detail-item--${k + 1}`}>
                      {d.tag && (
                        <span
                          className="rs-owa-detail-item__tag"
                          style={d.tagColor ? { color: d.tagColor, background: d.tagBg } : undefined}
                        >
                          [{d.tag}]
                        </span>
                      )}
                      {d.check && <span className="rs-owa-detail-item__check">&#10003;</span>}
                      {d.spinner && <span className="rs-owa-detail-item__spinner">&#9679;</span>}
                      <span className="rs-owa-detail-item__text">{d.text}</span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
          <Box className="rs-owa-summary">
            <span className="rs-owa-summary__check">&#10003;</span>{summary}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// =============================================================================
// THE COMPONENT.
// =============================================================================

export default function OfficeWindowAnimation({ app = 'powerpoint', scenario = 'pptx-deck', brand = 'restack' }) {
  const theme = useTheme();
  const sc = SCENARIOS[scenario] || SCENARIOS['pptx-deck'];
  const resolvedApp = app || sc.app;
  const appTheme = APP_THEME[resolvedApp] || APP_THEME.powerpoint;
  const Main = MAIN_RENDERERS[sc.main] || PptxDeckMain;
  const isClaude = brand === 'claude';

  return (
    <Box
      className="rs-owa-root"
      sx={{
        '--owa-accent': appTheme.color,
        '--owa-titlebar': appTheme.titlebar,
        fontFamily: theme.typography.fontFamily,
        width: '100%',
        maxWidth: 1040,
        mx: 'auto',
      }}
    >
      <style>{OWA_STYLES}</style>
      <Box className="rs-owa-window">
        {/* Title bar */}
        <Box className="rs-owa-titlebar">
          <Box className="rs-owa-titlebar__left">
            <Box className="rs-owa-titlebar__dots">
              <span className="rs-owa-dot" />
              <span className="rs-owa-dot" />
              <span className="rs-owa-dot" />
            </Box>
            {/* Quick-action control-centre buttons (mirrors the home hero). */}
            <Box className="rs-owa-titlebar__quick">
              <span className="rs-owa-quick-btn" />
              <span className="rs-owa-quick-btn" />
              <span className="rs-owa-quick-btn" />
            </Box>
          </Box>
          <Box className="rs-owa-titlebar__center">
            <span className="rs-owa-titlebar__badge">{appTheme.badge}</span>
            <span className="rs-owa-titlebar__text">{sc.filename}</span>
          </Box>
          <Box className="rs-owa-titlebar__right">
            <span className="rs-owa-titlebar__share">Share</span>
          </Box>
        </Box>

        {/* Ribbon */}
        <Box className="rs-owa-ribbon">
          {appTheme.ribbon.map((t, i) => (
            <span key={t} className={`rs-owa-ribbon__tab ${i === 1 ? 'rs-owa-ribbon__tab--active' : ''}`}>{t}</span>
          ))}
          <span className="rs-owa-ribbon__pill">
            {!isClaude && <brandConfig.icon sx={{ fontSize: '0.58rem' }} />}
            {isClaude ? 'Claude' : 'Open Restack'}
          </span>
        </Box>

        {/* Toolbar / control-centre strip (app-themed formatting buttons,
            mirrors the home hero's PowerPoint toolbar). */}
        <Box className="rs-owa-toolbar">
          <Box className="rs-owa-toolbar__group">
            <span className="rs-owa-toolbar__btn" />
            <span className="rs-owa-toolbar__btn" />
            <span className="rs-owa-toolbar__btn rs-owa-toolbar__btn--wide" />
          </Box>
          <span className="rs-owa-toolbar__sep" />
          <Box className="rs-owa-toolbar__group">
            <span className="rs-owa-toolbar__btn" />
            <span className="rs-owa-toolbar__btn" />
            <span className="rs-owa-toolbar__btn" />
          </Box>
          <span className="rs-owa-toolbar__sep" />
          <Box className="rs-owa-toolbar__group">
            <span className="rs-owa-toolbar__btn rs-owa-toolbar__btn--wide" />
            <span className="rs-owa-toolbar__btn" />
            <span className="rs-owa-toolbar__btn" />
          </Box>
          <span className="rs-owa-toolbar__sep" />
          <Box className="rs-owa-toolbar__group">
            <span className="rs-owa-toolbar__btn" />
            <span className="rs-owa-toolbar__btn rs-owa-toolbar__btn--wide" />
          </Box>
        </Box>

        {/* Body: [left nav | main content | integrated Restack sidebar] */}
        <Box className={`rs-owa-body rs-owa-body--${resolvedApp}`}>
          {resolvedApp === 'powerpoint' && <LeftNav app={resolvedApp} />}
          <Box className={`rs-owa-main rs-owa-main--${resolvedApp}`}>
            <Main />
          </Box>
          <Sidebar scenario={sc} brand={brand} />
        </Box>

        {/* Status bar */}
        <Box className="rs-owa-statusbar">
          <span>{sc.status}</span>
          <span className="rs-owa-statusbar__right">{appTheme.label}{isClaude ? '' : ' · Restack live'}</span>
        </Box>
      </Box>
    </Box>
  );
}

// =============================================================================
// CO-LOCATED STYLES. Namespaced `rs-owa-*` so there is zero collision with the
// home hero's `landing-hero__pptx-*` / `pptx-*` classes (which stay untouched).
// The 15s step-trail timing is ported faithfully from the home hero. Per-app
// accent colour is driven by the `--owa-accent` CSS variable set on the root.
// =============================================================================

const OWA_STYLES = `
.rs-owa-root { position: relative; }

/* ---- Window frame (16:9 outer artifact, unified 14px radius) ------------- */
.rs-owa-window {
  width: 100%;
  /* The whole app window explicitly holds a 16:9 screen ratio (the standard
     these tools run on); the body flexes to fill whatever the chrome leaves. */
  aspect-ratio: 16 / 9;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid rgba(46,20,10,0.08);
  box-shadow: 0 18px 44px rgba(46,20,10,0.14), 0 0 0 1px rgba(46,20,10,0.04);
  display: flex;
  flex-direction: column;
}

/* ---- Title bar (app-coloured) ------------------------------------------- */
.rs-owa-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.85rem;
  background: var(--owa-titlebar);
  position: relative;
}
.rs-owa-titlebar__left { display: flex; align-items: center; gap: 0.6rem; }
.rs-owa-titlebar__dots { display: flex; gap: 6px; }
.rs-owa-dot { width: 10px; height: 10px; border-radius: 50%; background: rgba(255,255,255,0.5); }
.rs-owa-titlebar__quick { display: flex; gap: 3px; }
.rs-owa-quick-btn { width: 14px; height: 14px; border-radius: 3px; background: rgba(255,255,255,0.22); }
.rs-owa-titlebar__center {
  display: flex; align-items: center; gap: 0.4rem;
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
}
.rs-owa-titlebar__badge {
  width: 15px; height: 15px; border-radius: 3px;
  background: rgba(255,255,255,0.24); color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.58rem; font-weight: 800;
}
.rs-owa-titlebar__text { font-size: 0.7rem; color: rgba(255,255,255,0.96); font-weight: 500; letter-spacing: 0.01em; }
.rs-owa-titlebar__right { display: flex; align-items: center; }
.rs-owa-titlebar__share {
  font-size: 0.56rem; font-weight: 700; color: var(--owa-accent);
  background: #fff; border-radius: 4px; padding: 0.18rem 0.6rem; letter-spacing: 0.02em;
}

/* ---- Ribbon ------------------------------------------------------------- */
.rs-owa-ribbon {
  display: flex; align-items: center; gap: 0;
  padding: 0 0.5rem; background: #f3f3f3; overflow: hidden;
}
.rs-owa-ribbon__tab {
  font-size: 0.58rem; font-weight: 500; color: #666;
  padding: 0.34rem 0.5rem; border-bottom: 2px solid transparent;
  white-space: nowrap; flex-shrink: 0;
}
.rs-owa-ribbon__tab--active { color: var(--owa-accent); border-bottom-color: var(--owa-accent); font-weight: 700; }
.rs-owa-ribbon__pill {
  margin-left: auto; display: inline-flex; align-items: center; gap: 0.28rem;
  flex-shrink: 0; padding: 0.18rem 0.55rem; margin: 0.18rem 0;
  border-radius: 999px; background: var(--owa-accent); color: #fff;
  font-size: 0.55rem; font-weight: 700; letter-spacing: 0.01em;
}

/* ---- Toolbar / control-centre strip (app-themed formatting buttons) ------ */
.rs-owa-toolbar {
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.28rem 0.75rem; background: #f8f8f8;
  border-top: 1px solid #ececec; border-bottom: 1px solid #e0e0e0;
}
.rs-owa-toolbar__group { display: flex; align-items: center; gap: 3px; }
.rs-owa-toolbar__btn {
  width: 16px; height: 16px; border-radius: 3px;
  background: color-mix(in srgb, var(--owa-accent) 16%, #ededed);
}
.rs-owa-toolbar__btn--wide { width: 28px; }
.rs-owa-toolbar__sep { width: 1px; height: 16px; background: #dcdcdc; }

/* ---- Body grid: [left nav | main | integrated sidebar] ------------------ */
.rs-owa-body {
  display: grid;
  /* Default = PowerPoint: slide-thumbnail rail | slide | agent sidebar.
     Excel and Word drop the rail (their own grid/page is the chrome). */
  grid-template-columns: 44px 1fr 244px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.rs-owa-body--excel,
.rs-owa-body--word { grid-template-columns: 1fr 244px; }

/* ---- Left nav variants -------------------------------------------------- */
.rs-owa-nav { background: #f0f0f0; border-right: 1px solid #e3e3e3; overflow: hidden; }
.rs-owa-nav--ppt { padding: 0.5rem 4px; display: flex; flex-direction: column; gap: 0.4rem; }
.rs-owa-nav__thumb { display: flex; align-items: flex-start; gap: 2px; }
.rs-owa-nav__thumb-num { font-size: 0.42rem; color: #999; width: 9px; text-align: right; flex-shrink: 0; margin-top: 1px; line-height: 1; }
.rs-owa-nav__thumb-slide { flex: 1; aspect-ratio: 16 / 9; background: #fff; border: 1px solid #ddd; border-radius: 2px; }
.rs-owa-nav__thumb--active .rs-owa-nav__thumb-slide { border-color: var(--owa-accent); border-width: 1.5px; box-shadow: 0 0 0 1px var(--owa-accent); }
.rs-owa-nav--xl { display: flex; flex-direction: column; }
.rs-owa-nav__rownum {
  flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center;
  font-size: 0.5rem; color: #9a9a9a; background: #f6f6f6;
  border-bottom: 1px solid #e6e6e6; font-variant-numeric: tabular-nums;
}
.rs-owa-nav--word { padding: 0.5rem 4px; display: flex; flex-direction: column; gap: 0.4rem; }
.rs-owa-nav__page { aspect-ratio: 3 / 4; background: #fff; border: 1px solid #ddd; border-radius: 2px; position: relative; }
.rs-owa-nav__page--active { border-color: var(--owa-accent); box-shadow: 0 0 0 1px var(--owa-accent); }
.rs-owa-nav__page-num { position: absolute; top: 1px; left: 2px; font-size: 0.4rem; color: #aaa; line-height: 1; }

/* ---- Main content area -------------------------------------------------- */
.rs-owa-main {
  background: #e8e8e8;
  display: flex; align-items: center; justify-content: center;
  padding: 0.4rem 0.5rem; overflow: hidden;
}
/* Excel has no editing "canvas": the cell grid fills the window edge to edge. */
.rs-owa-main--excel { background: #fff; padding: 0; align-items: stretch; justify-content: stretch; }

/* ===== PowerPoint slide ================================================== */
.rs-owa-slide {
  width: 100%; aspect-ratio: 16 / 9; background: #fff; border-radius: 8px;
  border: 1px solid rgba(46,20,10,0.06);
  padding: 0.6rem 0.85rem 0.5rem; display: flex; flex-direction: column;
  justify-content: space-between; gap: 0.12rem; position: relative; overflow: hidden;
}
.rs-owa-slide__title { font-size: 0.62rem; font-weight: 700; color: #1a1a1a; line-height: 1.2; letter-spacing: -0.01em; }
.rs-owa-slide__title--generic { font-size: 0.66rem; }
.rs-owa-slide__subtitle { font-size: 0.46rem; color: #888; font-weight: 500; }
.rs-owa-slide__divider { height: 2px; width: 100%; background: linear-gradient(90deg, var(--owa-accent), rgba(184,59,29,0.15)); border-radius: 2px; margin: 0.1rem 0; }
.rs-owa-slide__divider--generic { background: linear-gradient(90deg, var(--owa-accent), rgba(184,59,29,0.1)); }
.rs-owa-slide__metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.4rem; }
.rs-owa-slide__metric {
  display: flex; flex-direction: column; gap: 0.05rem;
  border-left: 2px solid rgba(184,59,29,0.3); padding-left: 0.35rem;
  opacity: 0; animation: owaReveal 15s ease infinite;
}
.rs-owa-slide__metric--1 { animation-delay: 0s; }
.rs-owa-slide__metric--2 { animation-delay: 0s; }
.rs-owa-slide__metric--3 { animation-delay: 0s; }
.rs-owa-slide__metric--1 { animation-name: owaMetric1; }
.rs-owa-slide__metric--2 { animation-name: owaMetric2; }
.rs-owa-slide__metric--3 { animation-name: owaMetric3; }
.rs-owa-slide__metric-label { font-size: 0.42rem; font-weight: 700; color: var(--owa-accent); letter-spacing: 0.06em; }
.rs-owa-slide__metric-value { font-size: 0.62rem; font-weight: 800; color: #1a1a1a; }
.rs-owa-slide__cite { font-size: 0.34rem; color: var(--owa-accent); margin-left: 1px; }
.rs-owa-slide__metric-desc { font-size: 0.38rem; color: #999; line-height: 1.25; }
.rs-owa-slide__chart-wrap { display: flex; flex-direction: column; gap: 0.15rem; }
.rs-owa-slide__chart-head { display: flex; justify-content: space-between; align-items: baseline; }
.rs-owa-slide__chart-title { font-size: 0.42rem; font-weight: 600; color: #555; }
.rs-owa-slide__chart-cagr { font-size: 0.4rem; font-weight: 700; color: var(--owa-accent); }
.rs-owa-slide__chart { display: flex; align-items: flex-end; gap: 0.5rem; height: 42px; }
.rs-owa-slide__bar {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end;
  height: 100%; position: relative;
}
.rs-owa-slide__bar::before {
  content: ''; width: 100%; height: var(--bar-h); border-radius: 2px 2px 0 0;
  background: rgba(184,59,29,0.4); transform-origin: bottom; transform: scaleY(0);
  animation: owaBarGrow 15s ease infinite;
}
.rs-owa-slide__bar--4::before { background: var(--owa-accent); }
.rs-owa-slide__bar--1::before { animation-delay: 0s; }
.rs-owa-slide__bar--2::before { animation-delay: 0.12s; }
.rs-owa-slide__bar--3::before { animation-delay: 0.24s; }
.rs-owa-slide__bar--4::before { animation-delay: 0.36s; }
.rs-owa-slide__bar-value { font-size: 0.34rem; font-weight: 700; color: #555; position: absolute; top: calc(100% - var(--bar-h) - 8px); }
.rs-owa-slide__bar-label { font-size: 0.36rem; color: #999; margin-top: 2px; }
.rs-owa-slide__sources { display: flex; flex-wrap: wrap; gap: 0.1rem 0.6rem; }
.rs-owa-slide__source {
  font-size: 0.36rem; color: #888; display: inline-flex; align-items: center; gap: 0.18rem;
  opacity: 0; animation: owaSourceReveal 15s ease infinite;
}
.rs-owa-slide__source--1 { animation-delay: 0s; }
.rs-owa-slide__source--2 { animation-delay: 0.1s; }
.rs-owa-slide__source--3 { animation-delay: 0.2s; }
.rs-owa-slide__source--4 { animation-delay: 0.3s; }
.rs-owa-slide__source-num {
  font-size: 0.3rem; font-weight: 700; color: #fff; background: var(--owa-accent);
  width: 8px; height: 8px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
}
.rs-owa-slide__pagenum { position: absolute; bottom: 0.4rem; right: 0.6rem; font-size: 0.4rem; color: #bbb; }

/* Generic deck variant */
.rs-owa-slide__cols { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; flex: 1; }
.rs-owa-slide__col {
  display: flex; flex-direction: column; gap: 0.18rem; padding: 0.4rem;
  border-radius: 8px; background: rgba(46,20,10,0.02); border: 1px solid rgba(46,20,10,0.05);
  opacity: 0; animation: owaReveal 15s ease infinite;
}
.rs-owa-slide__col--1 { animation-name: owaMetric1; }
.rs-owa-slide__col--2 { animation-name: owaMetric2; }
.rs-owa-slide__col--3 { animation-name: owaMetric3; }
.rs-owa-slide__col-head { font-size: 0.46rem; font-weight: 700; color: var(--owa-accent); margin-bottom: 0.12rem; }
.rs-owa-slide__col-line { height: 3px; border-radius: 2px; background: rgba(46,20,10,0.12); }
.rs-owa-slide__timeline { display: flex; gap: 3px; margin-top: 0.3rem; }
.rs-owa-slide__timeline-seg { height: 6px; border-radius: 3px; background: rgba(184,59,29,0.25); transform-origin: left; transform: scaleX(0); animation: owaBarGrow 15s ease infinite; }
.rs-owa-slide__timeline-seg--1 { animation-delay: 0s; }
.rs-owa-slide__timeline-seg--2 { animation-delay: 0.15s; }
.rs-owa-slide__timeline-seg--3 { animation-delay: 0.3s; }
.rs-owa-slide__timeline-seg--4 { animation-delay: 0.45s; background: var(--owa-accent); }

/* ===== Excel LBO model ================================================== */
.rs-owa-xl {
  width: 100%; height: 100%; background: #fff; border-radius: 8px;
  border: 1px solid rgba(46,20,10,0.06); display: flex; flex-direction: column; overflow: hidden;
}
.rs-owa-xl__formula {
  position: relative; display: flex; align-items: center; gap: 0.5rem;
  padding: 0.3rem 0.5rem; border-bottom: 1px solid rgba(46,20,10,0.07);
}
.rs-owa-xl__cellref {
  min-width: 26px; text-align: center; padding: 0.08rem 0.3rem; border-radius: 3px;
  border: 1px solid rgba(46,20,10,0.14); font-size: 0.48rem; font-weight: 600; color: rgba(46,20,10,0.65);
}
.rs-owa-xl__fx { font-style: italic; font-size: 0.5rem; color: rgba(46,20,10,0.4); }
.rs-owa-xl__formula-text { font-size: 0.5rem; color: rgba(46,20,10,0.7); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.rs-owa-xl__sweep {
  position: absolute; left: 0; bottom: 0; height: 2px; width: 28%;
  background: linear-gradient(90deg, transparent, var(--owa-accent), transparent);
  animation: owaXlSweep 3.4s ease-in-out infinite;
}
.rs-owa-xl__grid {
  flex: 1; min-height: 0; overflow: hidden; display: grid;
  grid-template-columns: 18px 1.1fr 0.7fr 1.1fr 0.7fr;
  grid-auto-rows: 26px;
  align-content: start;
  border-bottom: 1px solid rgba(46,20,10,0.06);
}
.rs-owa-xl__colhead {
  display: flex; align-items: center; justify-content: center;
  font-size: 0.46rem; font-weight: 600; color: rgba(46,20,10,0.42);
  background: rgba(46,20,10,0.04); border-bottom: 1px solid rgba(46,20,10,0.08); border-right: 1px solid rgba(46,20,10,0.05);
}
.rs-owa-xl__cell {
  display: flex; align-items: center; padding: 0 0.4rem;
  font-size: 0.5rem; font-weight: 500; color: rgba(46,20,10,0.72);
  border-right: 1px solid rgba(46,20,10,0.05); border-bottom: 1px solid rgba(46,20,10,0.05);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; position: relative;
}
.rs-owa-xl__cell--rownum { justify-content: center; padding: 0; font-size: 0.44rem; color: rgba(46,20,10,0.4); background: rgba(46,20,10,0.04); }
.rs-owa-xl__cell--header { font-weight: 700; color: rgba(46,20,10,0.75); background: rgba(33,115,70,0.07); }
.rs-owa-xl__cell--label { font-weight: 600; }
.rs-owa-xl__cell--num { justify-content: flex-end; font-variant-numeric: tabular-nums; color: var(--owa-accent); font-weight: 600; }
.rs-owa-xl__cell--calc { animation: owaXlCellPulse 2.8s ease-in-out infinite; z-index: 1; }
.rs-owa-xl__calc-val { display: inline-block; animation: owaXlFillIn 4.8s ease-in-out infinite; }
.rs-owa-xl__tabs { display: flex; align-items: center; padding: 0.2rem 0.5rem 0; background: rgba(46,20,10,0.025); }
.rs-owa-xl__tab { padding: 0.22rem 0.55rem; font-size: 0.48rem; font-weight: 500; color: rgba(46,20,10,0.5); border-bottom: 2px solid transparent; }
.rs-owa-xl__tab--active { font-weight: 700; color: var(--owa-accent); border-bottom-color: var(--owa-accent); background: #fff; }
.rs-owa-xl__tab-add { padding: 0 0.4rem; font-size: 0.56rem; color: rgba(46,20,10,0.35); }

/* ===== Word report ====================================================== */
/* A real A4 page sits at the top of the gray canvas and ENDS cleanly, with the
   gray continuing below it (as if the next page follows). The page is sized by
   height and given a portrait A4 ratio (1 : 1.414), so it never stretches to
   fill the body or cut content awkwardly. */
.rs-owa-doc {
  width: 100%; height: 100%; padding: 0.5rem 0;
  display: flex; align-items: flex-start; justify-content: center; overflow: hidden;
}
.rs-owa-doc__page {
  height: 100%; aspect-ratio: 1 / 1.414; max-width: 92%; background: #fff; border-radius: 8px;
  border: 1px solid rgba(46,20,10,0.06); box-shadow: 0 4px 14px rgba(46,20,10,0.08);
  padding: 0.7rem 0.85rem; display: flex; flex-direction: column; gap: 0.32rem; overflow: hidden;
}
.rs-owa-doc__h1 { font-size: 0.6rem; font-weight: 700; color: #1a1a1a; }
.rs-owa-doc__h2 { font-size: 0.52rem; font-weight: 700; color: #1a1a1a; margin-top: 0.18rem; }
.rs-owa-doc__line { height: 3px; border-radius: 2px; background: rgba(46,20,10,0.12); }
.rs-owa-doc__redline { position: relative; padding-left: 0.4rem; }
.rs-owa-doc__changebar { position: absolute; left: 0; top: 0; bottom: 0; width: 2px; background: var(--owa-accent); border-radius: 2px; animation: owaCaretBlink 1.1s step-end infinite; }
.rs-owa-doc__para { font-size: 0.48rem; line-height: 1.55; color: rgba(46,20,10,0.78); }
.rs-owa-doc__del { color: #C0392B; text-decoration: line-through; opacity: 0; animation: owaDocDel 15s ease infinite; }
.rs-owa-doc__ins { color: #2B579A; text-decoration: underline; font-weight: 600; opacity: 0; animation: owaDocIns 15s ease infinite; }
.rs-owa-doc__sup { font-size: 0.34rem; color: var(--owa-accent); }
/* Fact-check pill: appears after the insertion lands, marking the claim verified. */
.rs-owa-doc__verified {
  display: inline-block; margin-left: 0.2rem; padding: 0 0.2rem; border-radius: 3px;
  font-size: 0.34rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: #217346; background: rgba(33,115,70,0.12); opacity: 0;
  animation: owaDocIns 15s ease infinite;
}
/* Compact comparison block for the competitive landscape section. */
.rs-owa-doc__compare { display: flex; flex-direction: column; gap: 1px; margin-top: 0.1rem; }
.rs-owa-doc__crow {
  display: grid; grid-template-columns: 1.4fr 0.7fr 1fr; gap: 0.3rem;
  font-size: 0.44rem; color: rgba(46,20,10,0.78); padding: 0.12rem 0.2rem;
  border-bottom: 1px solid rgba(46,20,10,0.06);
}
.rs-owa-doc__crow--head { font-weight: 700; color: rgba(46,20,10,0.6); border-bottom-color: rgba(46,20,10,0.14); }
.rs-owa-doc__yes { color: #217346; font-weight: 700; }
.rs-owa-doc__no { color: rgba(46,20,10,0.45); }
.rs-owa-doc__caret { display: inline-block; width: 1px; height: 0.7em; vertical-align: middle; background: var(--owa-accent); margin: 0 1px; animation: owaCaretBlink 1.1s step-end infinite; }
.rs-owa-doc__footnotes { margin-top: auto; }
.rs-owa-doc__fn-rule { width: 30%; height: 1px; background: rgba(46,20,10,0.15); margin-bottom: 0.25rem; }
.rs-owa-doc__fn { font-size: 0.4rem; color: #888; }
.rs-owa-doc__fn sup { color: var(--owa-accent); }

/* ===== Integrated Restack sidebar ====================================== */
.rs-owa-sidebar {
  border-left: 1px solid #e3e3e3; background: #fafafa;
  display: flex; flex-direction: column; overflow: hidden;
}
.rs-owa-sidebar__header {
  display: flex; align-items: center; gap: 0.3rem;
  font-size: 0.6rem; font-weight: 600; color: #444;
  padding: 0.5rem 0.7rem; border-bottom: 1px solid #ececec; letter-spacing: 0.01em;
}
.rs-owa-sidebar__status-dot {
  margin-left: auto; width: 6px; height: 6px; border-radius: 50%;
  background: var(--owa-accent); box-shadow: 0 0 8px var(--owa-accent);
}
.rs-owa-sidebar__scroll {
  flex: 1; overflow-y: auto; padding: 0.55rem 0.7rem 0.7rem;
  display: flex; flex-direction: column; gap: 0.35rem;
  scrollbar-width: none; -ms-overflow-style: none;
}
.rs-owa-sidebar__scroll::-webkit-scrollbar { display: none; }

.rs-owa-msg {
  align-self: flex-end; max-width: 94%; margin-left: auto;
  background: #f0f0f0; border: 1px solid rgba(0,0,0,0.05);
  border-radius: 8px 8px 2px 8px; padding: 0.4rem 0.5rem;
  display: flex; flex-direction: column; gap: 0.35rem;
  opacity: 0; transform: translateY(18px); animation: owaUserMsg 15s ease infinite;
}
.rs-owa-msg__text { font-size: 0.55rem; line-height: 1.5; color: #333; }
.rs-owa-msg__attachments { display: flex; gap: 0.3rem; }
.rs-owa-msg__attachment {
  display: inline-flex; align-items: center; gap: 0.28rem;
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 5px;
  padding: 0.16rem 0.32rem 0.16rem 0.2rem; font-size: 0.5rem; color: #555; min-width: 0;
}
.rs-owa-msg__badge { font-size: 0.4rem; font-weight: 700; letter-spacing: 0.04em; padding: 0.12rem 0.22rem; border-radius: 2px; color: #fff; flex-shrink: 0; line-height: 1; }
.rs-owa-msg__name { font-size: 0.5rem; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }

.rs-owa-steps { display: flex; flex-direction: column; gap: 0.22rem; }

/* Each step card: hidden by default; per-step keyframes toggle visibility and
   the active/done state inside it (ported from the home hero's trail). */
.rs-owa-step {
  background: rgba(184,59,29,0.05); border-left: 2px solid var(--owa-accent);
  border-radius: 8px; padding: 0.3rem 0.45rem; display: flex; flex-direction: column; gap: 0.2rem; opacity: 0;
}
.rs-owa-step__row { display: flex; align-items: center; gap: 0.35rem; font-size: 0.56rem; color: var(--owa-accent); min-width: 0; }
.rs-owa-step__icon-stack { display: inline-grid; grid-template: 1fr / 1fr; width: 12px; height: 12px; flex-shrink: 0; }
.rs-owa-step__icon-stack > * { grid-area: 1 / 1; }
.rs-owa-step__icon {
  width: 12px; height: 12px; border-radius: 50%; background: var(--owa-accent); color: #fff;
  display: inline-flex; align-items: center; justify-content: center; line-height: 1;
  font-size: 0.52rem; font-weight: 700; opacity: 0;
}
.rs-owa-step__icon--spinner { background: rgba(184,59,29,0.18); }
.rs-owa-step__icon--spinner::before {
  content: ''; width: 5px; height: 5px; background: var(--owa-accent); border-radius: 50%;
  animation: owaStepPulse 0.9s ease-in-out infinite;
}
.rs-owa-step__label-stack { display: inline-grid; grid-template: 1fr / 1fr; flex: 1; min-width: 0; }
.rs-owa-step__label-stack > * { grid-area: 1 / 1; }
.rs-owa-step__label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0; }
.rs-owa-step__label--done { color: #555; }
.rs-owa-step__detail { display: flex; flex-direction: column; gap: 0.24rem; overflow: hidden; opacity: 0; max-height: 0; }

.rs-owa-detail-item { font-size: 0.54rem; color: #555; display: flex; align-items: center; gap: 0.3rem; opacity: 0; }
.rs-owa-detail-item__tag {
  font-size: 0.48rem; color: var(--owa-accent); background: rgba(184,59,29,0.08);
  padding: 0.04rem 0.32rem; border-radius: 3px; flex-shrink: 0; letter-spacing: 0.01em;
}
.rs-owa-detail-item__check { color: var(--owa-accent); font-size: 0.54rem; flex-shrink: 0; }
.rs-owa-detail-item__spinner { color: var(--owa-accent); font-size: 0.5rem; flex-shrink: 0; animation: owaDotBounce 0.8s ease-in-out infinite; }
.rs-owa-detail-item__text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }

.rs-owa-summary {
  display: flex; align-items: center; gap: 0.3rem;
  font-size: 0.5rem; color: #555; padding: 0.2rem 0.4rem; border-radius: 4px;
  background: rgba(184,59,29,0.04); margin-top: 0.15rem; opacity: 0; animation: owaSummary 15s ease infinite;
}
.rs-owa-summary__check { color: #16a34a; font-size: 0.56rem; font-weight: 800; flex-shrink: 0; }

/* ---- Status bar --------------------------------------------------------- */
/* The status bar is the window's bottom edge: it carries the same unified 14px
   radius as the top so the frame rounds consistently across every app (the
   outer overflow:hidden already clips, this keeps the fill flush to the curve). */
.rs-owa-statusbar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.3rem 0.85rem; background: #f0f0f0; font-size: 0.55rem; color: #777;
  border-bottom-left-radius: 14px; border-bottom-right-radius: 14px;
}
.rs-owa-statusbar__right { color: #999; }

/* =========================================================================
   KEYFRAMES. 6-step trail across a 15s loop. Each step i (0-indexed) gets an
   active window; the final step never flips to done (its spinner row stays
   live). Windows below mirror the home hero's pacing, generalised to 6 steps:
     s1 active 12-21  done 22+
     s2 active 23-33  done 34+
     s3 active 35-47  done 48+
     s4 active 49-60  done 61+
     s5 active 62-74  done 75+
     s6 active 76-100 (final: stays active, never done)
   ========================================================================= */

@keyframes owaStepPulse { 0%, 100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.25); opacity: 1; } }
@keyframes owaDotBounce { 0%, 100% { transform: translateY(0); opacity: 0.6; } 50% { transform: translateY(-2px); opacity: 1; } }

@keyframes owaUserMsg {
  0% { opacity: 0; transform: translateY(18px); }
  3% { opacity: 0; transform: translateY(18px); }
  7% { opacity: 1; transform: translateY(0); }
  100% { opacity: 1; transform: translateY(0); }
}

/* ---- Step 1 (active 12-21, done 22+) ---- */
.rs-owa-step--1 { animation: owaCard1 15s ease infinite; }
.rs-owa-step--1 .rs-owa-step__icon--spinner,
.rs-owa-step--1 .rs-owa-step__label--active { animation: owaActive1 15s ease infinite; }
.rs-owa-step--1 .rs-owa-step__icon--check,
.rs-owa-step--1 .rs-owa-step__label--done { animation: owaDone1 15s ease infinite; }
.rs-owa-step--1 .rs-owa-step__detail { animation: owaDetail1 15s ease infinite; }
.rs-owa-step--1 .rs-owa-detail-item--1 { animation: owaItem1a 15s ease infinite; }
.rs-owa-step--1 .rs-owa-detail-item--2 { animation: owaItem1b 15s ease infinite; }
.rs-owa-step--1 .rs-owa-detail-item--3 { animation: owaItem1c 15s ease infinite; }
.rs-owa-step--1 .rs-owa-detail-item--4 { animation: owaItem1d 15s ease infinite; }
@keyframes owaCard1 { 0%, 11% { opacity: 0; } 12% { opacity: 1; } 99.9% { opacity: 1; } 100% { opacity: 0; } }
@keyframes owaActive1 { 0%, 11% { opacity: 0; } 12% { opacity: 1; } 21% { opacity: 1; } 22% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaDone1 { 0%, 21% { opacity: 0; } 22% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaDetail1 { 0%, 11% { opacity: 0; max-height: 0; } 12% { opacity: 1; max-height: 90px; } 21% { opacity: 1; max-height: 90px; } 22% { opacity: 0; max-height: 0; } 100% { opacity: 0; max-height: 0; } }
@keyframes owaItem1a { 0%, 12.5% { opacity: 0; } 14% { opacity: 1; } 21% { opacity: 1; } 22% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem1b { 0%, 14% { opacity: 0; } 15.5% { opacity: 1; } 21% { opacity: 1; } 22% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem1c { 0%, 15.5% { opacity: 0; } 17% { opacity: 1; } 21% { opacity: 1; } 22% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem1d { 0%, 17% { opacity: 0; } 18.5% { opacity: 1; } 21% { opacity: 1; } 22% { opacity: 0; } 100% { opacity: 0; } }

/* ---- Step 2 (active 23-33, done 34+) ---- */
.rs-owa-step--2 { animation: owaCard2 15s ease infinite; }
.rs-owa-step--2 .rs-owa-step__icon--spinner,
.rs-owa-step--2 .rs-owa-step__label--active { animation: owaActive2 15s ease infinite; }
.rs-owa-step--2 .rs-owa-step__icon--check,
.rs-owa-step--2 .rs-owa-step__label--done { animation: owaDone2 15s ease infinite; }
.rs-owa-step--2 .rs-owa-step__detail { animation: owaDetail2 15s ease infinite; }
.rs-owa-step--2 .rs-owa-detail-item--1 { animation: owaItem2a 15s ease infinite; }
.rs-owa-step--2 .rs-owa-detail-item--2 { animation: owaItem2b 15s ease infinite; }
.rs-owa-step--2 .rs-owa-detail-item--3 { animation: owaItem2c 15s ease infinite; }
@keyframes owaCard2 { 0%, 22% { opacity: 0; } 23% { opacity: 1; } 99.9% { opacity: 1; } 100% { opacity: 0; } }
@keyframes owaActive2 { 0%, 22% { opacity: 0; } 23% { opacity: 1; } 33% { opacity: 1; } 34% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaDone2 { 0%, 33% { opacity: 0; } 34% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaDetail2 { 0%, 22% { opacity: 0; max-height: 0; } 23% { opacity: 1; max-height: 90px; } 33% { opacity: 1; max-height: 90px; } 34% { opacity: 0; max-height: 0; } 100% { opacity: 0; max-height: 0; } }
@keyframes owaItem2a { 0%, 23.5% { opacity: 0; } 25% { opacity: 1; } 33% { opacity: 1; } 34% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem2b { 0%, 25.5% { opacity: 0; } 27% { opacity: 1; } 33% { opacity: 1; } 34% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem2c { 0%, 27.5% { opacity: 0; } 29% { opacity: 1; } 33% { opacity: 1; } 34% { opacity: 0; } 100% { opacity: 0; } }

/* ---- Step 3 (active 35-47, done 48+) ---- */
.rs-owa-step--3 { animation: owaCard3 15s ease infinite; }
.rs-owa-step--3 .rs-owa-step__icon--spinner,
.rs-owa-step--3 .rs-owa-step__label--active { animation: owaActive3 15s ease infinite; }
.rs-owa-step--3 .rs-owa-step__icon--check,
.rs-owa-step--3 .rs-owa-step__label--done { animation: owaDone3 15s ease infinite; }
.rs-owa-step--3 .rs-owa-step__detail { animation: owaDetail3 15s ease infinite; }
.rs-owa-step--3 .rs-owa-detail-item--1 { animation: owaItem3a 15s ease infinite; }
.rs-owa-step--3 .rs-owa-detail-item--2 { animation: owaItem3b 15s ease infinite; }
.rs-owa-step--3 .rs-owa-detail-item--3 { animation: owaItem3c 15s ease infinite; }
@keyframes owaCard3 { 0%, 34% { opacity: 0; } 35% { opacity: 1; } 99.9% { opacity: 1; } 100% { opacity: 0; } }
@keyframes owaActive3 { 0%, 34% { opacity: 0; } 35% { opacity: 1; } 47% { opacity: 1; } 48% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaDone3 { 0%, 47% { opacity: 0; } 48% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaDetail3 { 0%, 34% { opacity: 0; max-height: 0; } 35% { opacity: 1; max-height: 90px; } 47% { opacity: 1; max-height: 90px; } 48% { opacity: 0; max-height: 0; } 100% { opacity: 0; max-height: 0; } }
@keyframes owaItem3a { 0%, 35.5% { opacity: 0; } 37% { opacity: 1; } 47% { opacity: 1; } 48% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem3b { 0%, 38% { opacity: 0; } 39.5% { opacity: 1; } 47% { opacity: 1; } 48% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem3c { 0%, 40.5% { opacity: 0; } 42% { opacity: 1; } 47% { opacity: 1; } 48% { opacity: 0; } 100% { opacity: 0; } }

/* ---- Step 4 (active 49-60, done 61+) ---- */
.rs-owa-step--4 { animation: owaCard4 15s ease infinite; }
.rs-owa-step--4 .rs-owa-step__icon--spinner,
.rs-owa-step--4 .rs-owa-step__label--active { animation: owaActive4 15s ease infinite; }
.rs-owa-step--4 .rs-owa-step__icon--check,
.rs-owa-step--4 .rs-owa-step__label--done { animation: owaDone4 15s ease infinite; }
.rs-owa-step--4 .rs-owa-step__detail { animation: owaDetail4 15s ease infinite; }
.rs-owa-step--4 .rs-owa-detail-item--1 { animation: owaItem4a 15s ease infinite; }
.rs-owa-step--4 .rs-owa-detail-item--2 { animation: owaItem4b 15s ease infinite; }
.rs-owa-step--4 .rs-owa-detail-item--3 { animation: owaItem4c 15s ease infinite; }
@keyframes owaCard4 { 0%, 48% { opacity: 0; } 49% { opacity: 1; } 99.9% { opacity: 1; } 100% { opacity: 0; } }
@keyframes owaActive4 { 0%, 48% { opacity: 0; } 49% { opacity: 1; } 60% { opacity: 1; } 61% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaDone4 { 0%, 60% { opacity: 0; } 61% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaDetail4 { 0%, 48% { opacity: 0; max-height: 0; } 49% { opacity: 1; max-height: 90px; } 60% { opacity: 1; max-height: 90px; } 61% { opacity: 0; max-height: 0; } 100% { opacity: 0; max-height: 0; } }
@keyframes owaItem4a { 0%, 49.5% { opacity: 0; } 51% { opacity: 1; } 60% { opacity: 1; } 61% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem4b { 0%, 52% { opacity: 0; } 53.5% { opacity: 1; } 60% { opacity: 1; } 61% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem4c { 0%, 54.5% { opacity: 0; } 56% { opacity: 1; } 60% { opacity: 1; } 61% { opacity: 0; } 100% { opacity: 0; } }

/* ---- Step 5 (active 62-74, done 75+) ---- */
.rs-owa-step--5 { animation: owaCard5 15s ease infinite; }
.rs-owa-step--5 .rs-owa-step__icon--spinner,
.rs-owa-step--5 .rs-owa-step__label--active { animation: owaActive5 15s ease infinite; }
.rs-owa-step--5 .rs-owa-step__icon--check,
.rs-owa-step--5 .rs-owa-step__label--done { animation: owaDone5 15s ease infinite; }
.rs-owa-step--5 .rs-owa-step__detail { animation: owaDetail5 15s ease infinite; }
.rs-owa-step--5 .rs-owa-detail-item--1 { animation: owaItem5a 15s ease infinite; }
.rs-owa-step--5 .rs-owa-detail-item--2 { animation: owaItem5b 15s ease infinite; }
.rs-owa-step--5 .rs-owa-detail-item--3 { animation: owaItem5c 15s ease infinite; }
@keyframes owaCard5 { 0%, 61% { opacity: 0; } 62% { opacity: 1; } 99.9% { opacity: 1; } 100% { opacity: 0; } }
@keyframes owaActive5 { 0%, 61% { opacity: 0; } 62% { opacity: 1; } 74% { opacity: 1; } 75% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaDone5 { 0%, 74% { opacity: 0; } 75% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaDetail5 { 0%, 61% { opacity: 0; max-height: 0; } 62% { opacity: 1; max-height: 90px; } 74% { opacity: 1; max-height: 90px; } 75% { opacity: 0; max-height: 0; } 100% { opacity: 0; max-height: 0; } }
@keyframes owaItem5a { 0%, 62.5% { opacity: 0; } 64% { opacity: 1; } 74% { opacity: 1; } 75% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem5b { 0%, 65% { opacity: 0; } 66.5% { opacity: 1; } 74% { opacity: 1; } 75% { opacity: 0; } 100% { opacity: 0; } }
@keyframes owaItem5c { 0%, 67.5% { opacity: 0; } 69% { opacity: 1; } 74% { opacity: 1; } 75% { opacity: 0; } 100% { opacity: 0; } }

/* ---- Step 6 (FINAL: active 76-100, never flips to done) ---- */
.rs-owa-step--6 { animation: owaCard6 15s ease infinite; }
.rs-owa-step--6 .rs-owa-step__icon--spinner,
.rs-owa-step--6 .rs-owa-step__label--active { animation: owaActive6 15s ease infinite; }
.rs-owa-step--6 .rs-owa-step__detail { animation: owaDetail6 15s ease infinite; }
.rs-owa-step--6 .rs-owa-detail-item--1 { animation: owaItem6a 15s ease infinite; }
.rs-owa-step--6 .rs-owa-detail-item--2 { animation: owaItem6b 15s ease infinite; }
.rs-owa-step--6 .rs-owa-detail-item--3 { animation: owaItem6c 15s ease infinite; }
@keyframes owaCard6 { 0%, 75% { opacity: 0; } 76% { opacity: 1; } 99.9% { opacity: 1; } 100% { opacity: 0; } }
@keyframes owaActive6 { 0%, 75% { opacity: 0; } 76% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaDetail6 { 0%, 75% { opacity: 0; max-height: 0; } 76% { opacity: 1; max-height: 90px; } 100% { opacity: 1; max-height: 90px; } }
@keyframes owaItem6a { 0%, 76.5% { opacity: 0; } 78% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaItem6b { 0%, 79% { opacity: 0; } 80.5% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaItem6c { 0%, 81.5% { opacity: 0; } 83% { opacity: 1; } 100% { opacity: 1; } }

/* Closing summary line: fades in near the end and holds. */
@keyframes owaSummary { 0%, 84% { opacity: 0; } 87% { opacity: 1; } 100% { opacity: 1; } }

/* ---- Main-content reveals (deck metrics, sources, bars, redline) -------- */
@keyframes owaReveal { 0% { opacity: 0; transform: translateY(4px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes owaMetric1 { 0%, 49% { opacity: 0; transform: translateY(4px); } 53% { opacity: 1; transform: translateY(0); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes owaMetric2 { 0%, 51% { opacity: 0; transform: translateY(4px); } 55% { opacity: 1; transform: translateY(0); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes owaMetric3 { 0%, 53% { opacity: 0; transform: translateY(4px); } 57% { opacity: 1; transform: translateY(0); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes owaBarGrow { 0%, 55% { transform: scaleY(0); } 66% { transform: scaleY(1); } 100% { transform: scaleY(1); } }
@keyframes owaSourceReveal { 0%, 76% { opacity: 0; } 82% { opacity: 1; } 100% { opacity: 1; } }

/* Excel computed-cell flourishes */
@keyframes owaXlSweep { 0% { transform: translateX(-110%); } 100% { transform: translateX(360%); } }
@keyframes owaXlCellPulse { 0%, 100% { box-shadow: 0 0 0 1.5px var(--owa-accent) inset; } 50% { box-shadow: 0 0 0 2.5px var(--owa-accent) inset; } }
@keyframes owaXlFillIn { 0%, 60% { opacity: 0; transform: translateY(3px); } 72%, 100% { opacity: 1; transform: translateY(0); } }

/* Word tracked-change redline: deletion fades, insertion replaces it */
@keyframes owaDocDel { 0%, 49% { opacity: 1; max-width: 200px; } 60% { opacity: 1; } 62% { opacity: 0.35; } 100% { opacity: 0.35; } }
@keyframes owaDocIns { 0%, 60% { opacity: 0; } 64% { opacity: 1; } 100% { opacity: 1; } }
@keyframes owaCaretBlink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }

/* =========================================================================
   RESPONSIVE. On narrow viewports the sidebar drops under the window body and
   the left nav collapses, so the whole thing stacks cleanly on phones.
   ========================================================================= */
/* Mobile keeps the FULL desktop animation, every internal detail intact (16:9
   window, slide-thumbnail rail, the integrated sidebar column), and just zooms
   the whole thing down to fit the phone, exactly like the landing hero scales
   its mockup. No reflow, no separate mobile layout: a fixed design width holds
   the desktop proportions and zoom shrinks both the render and the layout box
   so it fits without overflow and without any margin correction. */
@media (max-width: 720px) {
  .rs-owa-root {
    width: 1040px !important;
    max-width: none !important;
    zoom: 0.32;
  }
}
@media (max-width: 345px) {
  .rs-owa-root { zoom: 0.28; }
}

/* Respect reduced-motion: hold everything in its resting/visible state. */
@media (prefers-reduced-motion: reduce) {
  .rs-owa-window * { animation: none !important; }
  .rs-owa-step, .rs-owa-msg, .rs-owa-summary,
  .rs-owa-slide__metric, .rs-owa-slide__source, .rs-owa-slide__col,
  .rs-owa-detail-item, .rs-owa-step__label--done, .rs-owa-step__icon--check,
  .rs-owa-doc__ins { opacity: 1 !important; transform: none !important; }
  .rs-owa-step__label--active, .rs-owa-step__icon--spinner,
  .rs-owa-doc__del { opacity: 0 !important; }
  .rs-owa-step__detail { opacity: 1 !important; max-height: none !important; }
  .rs-owa-slide__bar::before, .rs-owa-slide__timeline-seg { transform: scaleY(1) scaleX(1) !important; }
}
`;
